/**
 * =============================================================================
 * WlRevenueTrendChart
 *
 * Evolucao de 12 meses do MRR. Suporta alternancia bar/line. O toggle pode
 * ser controlado externamente (via props `mode`/`onModeChange`) para permitir
 * renderizacao do botao fora da area do grafico (ex.: header do card).
 *
 * @version 1.5.0
 * RELEASE:
 *   2026-07-21 v1.0.0 - Versao inicial (somente barras).
 *   2026-07-21 v1.1.0 - Toggle bar/line integrado ao chart.
 *   2026-07-21 v1.2.0 - Default do toggle passa a ser linha.
 *   2026-07-21 v1.3.0 - Suporte a modo controlado; nao renderiza toggle interno
 *                       quando `mode`/`onModeChange` sao passados por prop.
 *   2026-07-22 v1.4.0 - Composicao Planos + Add-ons: barras empilhadas por
 *                       origem e tooltip detalhado no modo linha (Planos,
 *                       Add-ons e Total). Consome `mrr_plans` e `mrr_addons`
 *                       expostos pela RPC `wl_get_financial_kpis` v1.7.0.
 *                       Fallback: quando ausentes, cai para o total unico.
 *   2026-07-22 v1.5.0 - Paleta dedicada para o modo barras (roxo p/ Planos e
 *                       ambar p/ Add-ons) inspirada em dashboards financeiros
 *                       de referencia. Cores locais ao chart — nao afeta
 *                       tokens globais do design system.
 * =============================================================================
 */
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, LineChart as LineIcon } from 'lucide-react';
import { WlFinancialRevenuePoint } from '@/hooks/wl/useWlFinancialKpis';
import { ChartTypeToggle, ChartTypeMode } from './ChartTypeToggle';

// Formatador BRL compacto reutilizado em eixos, legendas e tooltip.
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

// Paleta dedicada ao modo barras (nao afeta tokens globais). Roxo para Planos
// (receita recorrente principal) e ambar para Add-ons (receita complementar).
const COR_PLANOS = '#8b7cf6';
const COR_ADDONS = '#f5a524';

// Formatador BRL com centavos para o tooltip (maior precisao).
const fmtBRLPreciso = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(v || 0));

interface Props {
  data: WlFinancialRevenuePoint[];
  /** Modo controlado externamente (opcional). */
  mode?: ChartTypeMode;
  /** Callback para modo controlado (opcional). */
  onModeChange?: (mode: ChartTypeMode) => void;
}

/**
 * Tooltip customizado — mostra Planos, Add-ons e o MRR total quando a RPC
 * expoe a composicao (v1.7.0+). Fallback exibe apenas o total.
 */
function CompositionTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;
  const row = (payload[0]?.payload ?? {}) as WlFinancialRevenuePoint;
  const planos = Number(row.mrr_plans ?? NaN);
  const addons = Number(row.mrr_addons ?? NaN);
  const total = Number(row.mrr ?? 0);
  const temComposicao = Number.isFinite(planos) && Number.isFinite(addons);

  return (
    <div
      style={{
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        borderRadius: 8,
        fontSize: 12,
        padding: '8px 10px',
        minWidth: 180,
      }}
    >
      <div className="font-medium mb-1">{`Mês ${label}`}</div>
      {temComposicao && (
        <>
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ background: COR_PLANOS }}
              />
              Planos
            </span>
            <span>{fmtBRLPreciso(planos)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ background: COR_ADDONS }}
              />
              Add-ons
            </span>
            <span>{fmtBRLPreciso(addons)}</span>
          </div>
          <div
            className="mt-1 pt-1 flex items-center justify-between gap-4 font-semibold"
            style={{ borderTop: '1px solid hsl(var(--border))' }}
          >
            <span>MRR total</span>
            <span>{fmtBRLPreciso(total)}</span>
          </div>
        </>
      )}
      {!temComposicao && (
        <div className="flex items-center justify-between gap-4">
          <span>MRR total</span>
          <span>{fmtBRLPreciso(total)}</span>
        </div>
      )}
    </div>
  );
}

/** Grafico de evolucao de receita (12 meses) — barras ou linhas. */
export function WlRevenueTrendChart({ data, mode: modeProp, onModeChange }: Props) {
  // Estado interno usado apenas quando o consumidor nao controla o modo.
  const [internalMode, setInternalMode] = useState<ChartTypeMode>('line');
  const isControlled = modeProp !== undefined && onModeChange !== undefined;
  const mode: ChartTypeMode = isControlled ? modeProp! : internalMode;

  // Verifica se ha composicao (RPC v1.7.0+). Se ao menos uma linha traz
  // mrr_plans/mrr_addons definidos, ativamos barras empilhadas + legenda.
  const temComposicao = (data ?? []).some(
    (d) => d.mrr_plans !== undefined || d.mrr_addons !== undefined,
  );

  return (
    <div className="relative w-full h-64">
      {/* Toggle interno apenas quando nao controlado — evita duplicar botao */}
      {!isControlled && (
        <div className="absolute right-0 top-0 z-10">
          <ChartTypeToggle value={mode} onChange={setInternalMode} />
        </div>
      )}

      <ResponsiveContainer width="100%" height="100%">
        {mode === 'bar' ? (
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtBRL(Number(v))} width={80} />
            <Tooltip content={<CompositionTooltip />} />
            {temComposicao ? (
              <>
                {/* Legenda so aparece no modo com composicao (Planos + Add-ons). */}
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
                <Bar
                  dataKey="mrr_plans"
                  name="Planos"
                  stackId="mrr"
                  fill={COR_PLANOS}
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="mrr_addons"
                  name="Add-ons"
                  stackId="mrr"
                  fill={COR_ADDONS}
                  radius={[4, 4, 0, 0]}
                />
              </>
            ) : (
              <Bar
                dataKey="mrr"
                name="MRR total"
                fill={COR_PLANOS}
                radius={[4, 4, 0, 0]}
              />
            )}
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtBRL(Number(v))} width={80} />
            <Tooltip content={<CompositionTooltip />} />
            <Line
              type="monotone"
              dataKey="mrr"
              name="MRR total"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// Reexport dos icones para consumidores que quiserem estilizar o toggle.
export { BarChart3, LineIcon };
